import React from 'react';
import intl from 'react-intl-universal';
import { MenuItem } from '@blueprintjs/core';
import { Suggest } from '@blueprintjs/select';
import { FormattedMessage as T } from '@/components/FormattedMessage';

import classNames from 'classnames';
import { CLASSES } from '@/constants/classes';

/**
 * branch suggest field.
 * @returns
 */
export function BranchSuggestField({
  branches,
  initialBranchId,
  selectedBranchId,
  defaultSelectText = intl.get('select_branch'),
  popoverFill = false,
  onBranchSelected,
  ...suggestProps
}: any) {
  const initialBranch = React.useMemo(
    () => branches.find((b: any) => b.id === initialBranchId),
    [initialBranchId, branches],
  );

  const [selectedBranch, setSelectedBranch] = React.useState(
    initialBranch || null,
  );
  const [isOpen, setIsOpen] = React.useState(false);

  React.useEffect(() => {
    if (typeof selectedBranchId !== 'undefined') {
      const branch = selectedBranchId
        ? branches.find((a: any) => a.id === selectedBranchId)
        : null;
      setSelectedBranch(branch);
    }
  }, [selectedBranchId, branches, setSelectedBranch]);

  /**
   *
   * @param {*} branch
   * @returns
   */
  const branchItemRenderer = (
    branch: any,
    { handleClick, modifiers, query }: any,
  ) => {
    return (
      <MenuItem
        // active={modifiers.active}
        disabled={modifiers.disabled}
        label={branch.code}
        key={branch.id}
        onClick={handleClick}
        text={branch.name}
      />
    );
  };

  /**
   *
   * @param {*} query
   * @param {*} branch
   * @param {*} _index
   * @param {*} exactMatch
   * @returns
   */
  const branchItemPredicate = (
    query: any,
    branch: any,
    _index: any,
    exactMatch: any,
  ) => {
    const normalizedTitle = branch.name.toLowerCase();
    const normalizedQuery = query.toLowerCase();

    if (exactMatch) {
      return normalizedTitle === normalizedQuery;
    } else {
      return `${branch.code}. ${normalizedTitle}`.indexOf(normalizedQuery) >= 0;
    }
  };

  /**
   *
   * @param {*} branch
   * @returns
   */
  const brnachItemSelect = React.useCallback(
    (branch: any) => {
      if (branch.id) {
        setSelectedBranch({ ...branch });
        onBranchSelected && onBranchSelected(branch);
        setIsOpen(false);
      }
    },
    [setSelectedBranch, onBranchSelected],
  );

  // Handle input click to open dropdown
  const handleInputClick = React.useCallback(() => {
    setIsOpen(true);
  }, []);

  /**
   *
   * @param {*} inputVaue
   * @returns
   */
  const branchInputValueRenderer = (inputValue: any) => {
    if (inputValue) {
      return inputValue.name.toString();
    }
    return '';
  };

  // Merge input props with onClick handler
  const mergedInputProps = {
    placeholder: defaultSelectText,
    onClick: handleInputClick,
    ...suggestProps.inputProps,
  };

  // Merge popover props with isOpen state
  const mergedPopoverProps = {
    minimal: true,
    usePortal: false,
    isOpen: isOpen,
    onInteraction: (nextOpenState: any) => setIsOpen(nextOpenState),
    ...suggestProps.popoverProps,
  };

  return (
    <Suggest
      items={branches}
      noResults={<MenuItem disabled={true} text={<T id={'no_accounts'} />} />}
      itemRenderer={branchItemRenderer}
      itemPredicate={branchItemPredicate}
      onItemSelect={brnachItemSelect}
      selectedItem={selectedBranch}
      inputProps={mergedInputProps}
      resetOnClose={true}
      fill={true}
      popoverProps={mergedPopoverProps}
      inputValueRenderer={branchInputValueRenderer}
      className={classNames(CLASSES.FORM_GROUP_LIST_SELECT, {
        [CLASSES.SELECT_LIST_FILL_POPOVER]: popoverFill,
      })}
      {...suggestProps}
    />
  );
}
